/**
 * @module db
 * @description Database utilities: adapters, schema helpers, migrations, and query interceptors
 *
 * @dependencies
 * - core/ (logger, events/emitter, utils/paths)
 * - engine/ (loader, context) - only in CLI scripts (migrate.ts)
 * - runtime/ (entity-factory) - only in seed-runner.ts
 *
 * @note This module should ideally only depend on core/ utilities.
 *       Dependencies on engine/ and runtime/ are for CLI tooling only.
 */

// Schema helpers (timestamps, audit fields, column utilities)
export {
  getDbClient,
  formatTimestamp,
  nowTimestamp,
  addTimestamps,
  addAuditFieldsIfMissing,
  addSoftDeleteFieldIfMissing,
  addConfigDefaultField,
  addColumnIfMissing
} from './schema-helpers.js'

// SQL parsing utilities
export {
  extractTableFromSql,
  extractTableFromSelect,
  extractTableFromInsert,
  extractTableFromUpdate,
  extractTableFromDelete
} from './sql-utils.js'

// SQLite compatibility (boolean 0/1 → true/false conversion)
export {
  createSqliteBooleanProcessor,
  registerBooleanColumn,
  convertBooleans
} from './sqlite-compat.js'
export type { SqliteBooleanProcessor } from './sqlite-compat.js'

// Module execution utilities
export { runModuleSeed } from './seed-runner.js'

// System tables
export { ensureSystemTables } from './ensure-system-tables.js'

// Migration sources (multi-source: core → plugins → project)
export {
  buildMigrationSources,
  type MigrationSourceId,
  type MigrationSourceDescriptor
} from './migration-sources.js'

// Migration system
export {
  runMigrations,
  loadAllMigrationFiles,
  rollbackLastBatch,
  rollbackByName,
  showMigrationStatus,
  resetDatabase,
  type MigrationFile,
  type MigrationStatus
} from './migration-runner.js'

// Migration generator
export { generateMigrationForSource, type MigrationScope } from './migration-generator.js'

// Migration engine (direct table creation from entity definitions — for tests)
export { runGeneratedMigration, runAllGeneratedMigrations } from './migration-engine.js'

// Query interceptor
export { setupQueryInterceptor, type DbEventPayload } from './query-interceptor.js'

// Database adapters
export { KnexAdapter, createKnexAdapter } from './knex-adapter.js'
export { InMemoryAdapter, createInMemoryAdapter } from './memory-adapter.js'
export { RedisAdapter, createRedisAdapter, type RedisAdapterOptions } from './redis-adapter.js'

// Schema adapters
export { KnexSchemaAdapter, createKnexSchemaAdapter } from './schema-adapter.js'
export { InMemorySchemaAdapter, createInMemorySchemaAdapter } from './memory-schema-adapter.js'

// Filter helpers
export {
  applyFilters,
  applyFilterOperators,
  applyInMemoryFilters,
  matchFilterOperators
} from './filter-helpers.js'

// Memory Knex (SQLite :memory: for adapter: 'memory' entities)
export { getMemoryKnex, destroyMemoryKnex } from './memory-knex.js'

// Connection (Knex instance)
export { db, getDb, initDb, destroyDb } from './connection.js'

// Query helpers (pagination, search)
export {
  getPagination,
  buildPaginatedResult,
  getSearchableFields,
  applySearchFilter,
  type PaginationParams
} from './query-helpers.js'

// SQLite post-process (additional)
export { sqlitePostProcess } from './sqlite-compat.js'

// Memory table builder (creates tables in :memory: at boot)
export { createMemoryTables } from './memory-table-builder.js'
